// Pure presentation component for AI moderation banners. Extracts the visual
// rendering from structure/question/moderation/moderation-report.tsx so it can
// be tested independently via Storybook.

import { Send } from '@carbon/icons-react';
import type { ModerationRule } from '@repo/shared/community-guidelines';
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { AITag } from 'component/aitag';
import { HorizBox, Pad, PadBox, VertBox } from 'component/basics';
import { CTAButton } from 'component/button';
import { fromNow } from 'component/date';
import { BulletText, RichText } from 'component/richtext';
import { Heading, UtilityText } from 'component/text';
import { translateLabel, useSiloLanguage } from 'component/translation';
import { AccordionCard } from 'component/ui/accordion-card';
import React from 'react';
import { Text, View } from 'react-native';
import { getParentRuleByChildKey, getRuleLabelByKey } from 'util/community-guidelines';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

/**
 * The moderation state to display: a violation's specifics (guideline
 * keys, severity, whether the moderation system was down) plus, once
 * appealed, whether it's still pending a human decision. `ModerationBanner`
 * is only ever rendered when there IS a violation to show — callers resolve
 * this from a live post-attempt rejection or a comment's own stored
 * moderation state, never from a raw comment. `violations` are the stable
 * `ModerationRule` keys both comment and perspective moderation record;
 * this component resolves each to its guideline text itself.
 */
export type ModerationViolation = {
    violations?: ModerationRule[];
    moderationSystemDown?: boolean;
    pending?: boolean;
    time?: string | number;
};

export type ModerationBannerProps = {
    violation: ModerationViolation;
    isPerspectiveModeration?: boolean;
    isAdminBypassing?: boolean;
    showExtraModerationMessage?: boolean;
    onSendToModerator?: () => void;
    onShowCommunityGuidelines?: () => void;
};

export function ModerationBanner({
    violation,
    isPerspectiveModeration = false,
    isAdminBypassing = false,
    showExtraModerationMessage = false,
    onSendToModerator,
    onShowCommunityGuidelines,
}: ModerationBannerProps) {
    const { colors } = useTheme();
    const language = useSiloLanguage();

    if (isAdminBypassing) {
        return <AdminBypassingBannerContent violations={violation.violations} />;
    }

    const moderatedThingLabel = isPerspectiveModeration
        ? t('moderation.perspective_label', 'perspective label')
        : t('moderation.response', 'response');
    const moderatedThing = translateLabel({ label: moderatedThingLabel, language });
    const titleMessage = t('moderation.check_your_thing', '⚠️ Check your {moderatedThing}');

    const warningMessage = t(
        'moderation.incompatible_warning',
        'AI review found that your {moderatedThing} may be incompatible with the [Community Guidelines]($onPress): ',
    );

    if (violation.moderationSystemDown) {
        return <SystemDownBannerContent onSendToModerator={onSendToModerator} />;
    }

    return (
        <View style={{ borderRadius: 8, backgroundColor: colors.colorSurfaceBannerWarning }} testID='moderation-report'>
            <PadBox horiz={20} vert={16}>
                <VertBox gap={16} wide>
                    <ViolationBannerContent
                        moderatedThing={moderatedThing}
                        onShowCommunityGuidelines={onShowCommunityGuidelines}
                        titleMessage={titleMessage}
                        violations={violation.violations}
                        warningMessage={warningMessage}
                    />
                    <AiAppealSection
                        isPerspectiveModeration={isPerspectiveModeration}
                        onSendToModerator={onSendToModerator}
                        pending={!!violation.pending}
                        showExtraModerationMessage={showExtraModerationMessage}
                        time={violation.time}
                    />
                </VertBox>
            </PadBox>
        </View>
    );
}

function SystemDownBannerContent({ onSendToModerator }: { onSendToModerator?: () => void }) {
    const { colors } = useTheme();
    return (
        <View
            style={{ backgroundColor: colors.colorSurfaceBannerWarning, paddingHorizontal: 20, paddingVertical: 16, gap: 20 }}
            testID='system-down-banner'
        >
            <VertBox gap={16} wide>
                <AITag />
                <Heading
                    color={colors.colorTextSecondaryOnBanner}
                    label={t('moderation.system_unavailable_title', '⚠️ Moderation system temporarily unavailable')}
                />
                <RichText
                    color={colors.colorTextSecondaryOnBanner}
                    label={t(
                        'moderation.system_unavailable_message',
                        'Our AI moderation system is temporarily unavailable. You can still send your post to a human moderator for review.',
                    )}
                    lineHeight={1.25}
                />
            </VertBox>
            <CTAButton
                label={t('moderation.send_for_review_button', 'Send to a moderator for review')}
                leftIcon={<Send />}
                onPress={() => onSendToModerator?.()}
                type='secondary'
                wide
            />
        </View>
    );
}

function AdminBypassingBannerContent({ violations }: { violations?: ModerationRule[] }) {
    const { colors } = useTheme();
    return (
        <View
            style={{
                gap: 32,
            }}
            testID='admin-bypassing-banner'
        >
            {violations && (
                <VertBox gap={12} wide>
                    <UtilityText label={t('moderation.ai_flag', 'AI flag')} type='small' weight='medium' />
                    <ViolationAccordionList violations={violations} />
                </VertBox>
            )}

            <View
                style={{
                    borderRadius: 8,
                    backgroundColor: colors.colorSurfaceBannerInfo,
                    paddingHorizontal: 20,
                    paddingVertical: 16,
                    gap: 16,
                }}
            >
                <HorizBox center gap={4}>
                    <UtilityText color={colors.colorTextSecondaryOnBanner} text='📧' type='large' weight='medium' />
                    <UtilityText
                        color={colors.colorTextSecondaryOnBanner}
                        label={t('moderation.ai_wrong_question', 'Did AI get it wrong?')}
                        type='large'
                        weight='medium'
                    />
                </HorizBox>
                <UtilityText
                    color={colors.colorTextSecondaryOnBanner}
                    label={t(
                        'moderation.admin_filter_notice',
                        'To help improve the AI moderation, send a screenshot of your post and this message to your team.',
                    )}
                />
            </View>
        </View>
    );
}

type ViolationBannerContentProps = {
    violations?: ModerationRule[];
    moderatedThing: string;
    titleMessage: TLabel;
    warningMessage: TLabel;
    onShowCommunityGuidelines?: () => void;
};

function ViolationBannerContent({
    violations,
    moderatedThing,
    titleMessage,
    warningMessage,
    onShowCommunityGuidelines,
}: ViolationBannerContentProps) {
    const { colors } = useTheme();
    const textColor = colors.colorTextSecondaryOnBanner;
    return (
        <>
            <AITag />
            <Heading color={textColor} formatParams={{ moderatedThing }} label={titleMessage} />
            <Text style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                <RichText
                    callbacks={{ onPress: () => onShowCommunityGuidelines?.() }}
                    color={textColor}
                    formatParams={{ moderatedThing }}
                    label={warningMessage}
                    lineHeight={1.25}
                />
            </Text>
            <ViolationAccordionList violations={violations} />
        </>
    );
}

type AiAppealSectionProps = {
    pending: boolean;
    time?: string | number;
    isPerspectiveModeration: boolean;
    showExtraModerationMessage: boolean;
    onSendToModerator?: () => void;
};

function AiAppealSection({ pending, time, isPerspectiveModeration, showExtraModerationMessage, onSendToModerator }: AiAppealSectionProps) {
    const { colors } = useTheme();
    const s = useModerationBannerStyles();
    if (isPerspectiveModeration) return null;

    if (pending) {
        return (
            <View style={{ flexDirection: 'row', gap: 4 }}>
                {React.createElement(Send, { style: { color: colors.colorTextSecondaryOnBanner } })}
                <UtilityText
                    color={colors.colorTextSecondaryOnBanner}
                    formatParams={{ time: fromNow(time) }}
                    label={t('moderation.sent_to_moderator_time', 'Sent to moderator: {time}')}
                    type='tiny'
                />
            </View>
        );
    }

    return (
        <View>
            <View style={s.line} />
            <Pad size={20} />
            <UtilityText
                color={colors.colorTextSecondaryOnBanner}
                label={t('moderation.ai_wrong_question', 'Did AI get it wrong?')}
                weight='medium'
            />
            {showExtraModerationMessage && (
                <View>
                    <Pad size={8} />
                    <UtilityText
                        label={t(
                            'moderation.strict_filter_notice',
                            '⚠️ Our moderation filter is currently set to be quite strict in order to ensure the highest level of safety! If you feel that your contribution is being unfairly rejected, please contact our moderation team!',
                        )}
                    />
                </View>
            )}
            <Pad size={12} />
            <CTAButton
                label={t('moderation.send_for_review_button', 'Send to a moderator for review')}
                leftIcon={<Send />}
                onPress={() => onSendToModerator?.()}
                type='secondary'
                wide
            />
        </View>
    );
}

function ViolationAccordionList({ violations }: { violations?: ModerationRule[] }) {
    const parentViolations: (TLabel | null)[] = Array.from(new Set(violations?.map(getParentRuleByChildKey) ?? []));

    return (
        <VertBox gap={16} wide>
            {parentViolations
                .filter((parent) => !!parent)
                .map((parent, index) => (
                    <AccordionCard
                        highlighted
                        key={parent.key}
                        testID={`accordion-violation-${index}`}
                        title={<UtilityText label={parent || undefined} weight='strong' />}
                    >
                        {violations
                            ?.filter((violation) => getParentRuleByChildKey(violation)?.key === parent?.key)
                            .map((violation) => (
                                <BulletText key={violation} label={getRuleLabelByKey(violation)} />
                            ))}
                    </AccordionCard>
                ))}
        </VertBox>
    );
}

const useModerationBannerStyles = createThemedStyles((tokens) => ({
    line: {
        height: 1,
        backgroundColor: tokens.colors.colorBorderPrimary,
        width: '100%',
    },
}));
